import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Target, Users, Lightbulb, Heart } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Hero } from '@/components/common/Hero';
import { useTranslation } from '@/features/i18n';

export function AboutPage() {
  const { t } = useTranslation();

  const values = [
    {
      icon: Target,
      title: t.about.values.quality,
      description: t.about.values.qualityDesc,
    },
    {
      icon: Users,
      title: t.about.values.partnership,
      description: t.about.values.partnershipDesc,
    },
    {
      icon: Lightbulb,
      title: t.about.values.innovation,
      description: t.about.values.innovationDesc,
    },
    {
      icon: Heart,
      title: t.about.values.passion,
      description: t.about.values.passionDesc,
    },
  ];

  const stats = [
    { value: '100+', label: t.home.stats.customers },
    { value: '250+', label: t.home.stats.projects },
    { value: '98%', label: t.home.stats.satisfaction },
    { value: '5+', label: t.home.whyUs.experience },
  ];

  return (
    <>
      <SEO
        title={t.about.title}
        description={t.about.description}
      />

      <Hero
        title={t.about.title}
        subtitle={t.about.subtitle}
        description={t.about.description}
        size="md"
        variant="light"
      />

      {/* Story section - WHITE background with GRAY card */}
      <section className="ninito-section bg-white">
        <div className="ninito-container">
          <div className="ninito-two-col">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
                Vår historie
              </span>
              <h2 className="mt-4 text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-black tracking-[-0.02em]">
                {t.about.story.title}
              </h2>
              <div className="mt-6 space-y-5 text-lg text-[#666] leading-relaxed">
                <p>{t.about.story.content}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-[32px] bg-[#edf1f5] p-8 lg:p-12 relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#0078ff]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0078ff]/5 rounded-full blur-2xl" />

                <div className="relative h-full flex flex-col justify-center items-center text-center">
                  <div className="text-[72px] md:text-[84px] lg:text-[100px] font-bold text-black tracking-tight">
                    5+
                  </div>
                  <div className="mt-2 text-xl text-[#666]">
                    {t.home.whyUs.experience}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values section - GRAY background with WHITE cards */}
      <section className="ninito-section bg-[#edf1f5]">
        <div className="ninito-container">
          <div className="max-w-[680px] mb-16">
            <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
              Verdier
            </span>
            <h2 className="mt-4 text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-black tracking-[-0.02em]">
              {t.about.values.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="ninito-card h-full ninito-hover-lift">
                  <div className="w-14 h-14 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center mb-5">
                    <value.icon className="w-7 h-7 text-[#0078ff]" />
                  </div>
                  <h3 className="text-xl font-semibold text-black tracking-tight">
                    {value.title}
                  </h3>
                  <p className="mt-3 text-[#666] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us - WHITE background */}
      <section className="ninito-section bg-white">
        <div className="ninito-container">
          <div className="ninito-two-col">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
                Hvorfor oss
              </span>
              <h2 className="mt-4 text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-black tracking-[-0.02em]">
                {t.about.whyUs.title}
              </h2>
              <p className="mt-5 text-lg text-[#666] leading-relaxed">
                {t.about.whyUs.description}
              </p>

              <div className="mt-10 space-y-5">
                {t.home.whyUs.points.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#0078ff]" />
                    </div>
                    <span className="text-[#666] text-base leading-relaxed">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Stats grid - ninito style */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-7"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="ninito-card text-center py-8 border border-[#edf1f5]">
                    <div className="text-[40px] md:text-[48px] font-bold text-black tracking-tight">
                      {stat.value}
                    </div>
                    <div className="mt-2 text-[#666]">
                      {stat.label}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA - DARK background */}
      <section className="ninito-cta">
        <div className="relative ninito-container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.home.cta.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-lg md:text-xl text-[#999] max-w-[600px] mx-auto"
          >
            {t.home.cta.description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <Link to="/contact" className="ninito-btn ninito-btn-white ninito-btn-lg group">
              {t.home.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
